import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { PRICE_SETTINGS_API, REGISTRATION_RULES_API, fetchWithAuth } from '../utils/api';
import { calculateTotalPrice } from '../utils/priceCalculations';

// Initial state for the context
const initialState = {
  isLoading: false,
  notification: null,
  hasUnsavedChanges: false,
  existingSettings: null,
  ageGroups: [],
  isLoadingAgeGroups: false
};

// Create the context
const PricingSettingsContext = createContext();

// Define action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  SET_UNSAVED_CHANGES: 'SET_UNSAVED_CHANGES',
  SET_EXISTING_SETTINGS: 'SET_EXISTING_SETTINGS',
  SET_AGE_GROUPS: 'SET_AGE_GROUPS',
  SET_LOADING_AGE_GROUPS: 'SET_LOADING_AGE_GROUPS',
  RESET_NOTIFICATION: 'RESET_NOTIFICATION'
};

// Reducer function to handle state updates
function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case ACTIONS.SET_NOTIFICATION:
      return { ...state, notification: action.payload };
    case ACTIONS.RESET_NOTIFICATION:
      return { ...state, notification: null };
    case ACTIONS.SET_UNSAVED_CHANGES:
      return { ...state, hasUnsavedChanges: action.payload };
    case ACTIONS.SET_EXISTING_SETTINGS:
      return { ...state, existingSettings: action.payload };
    case ACTIONS.SET_AGE_GROUPS:
      return { ...state, ageGroups: action.payload };
    case ACTIONS.SET_LOADING_AGE_GROUPS:
      return { ...state, isLoadingAgeGroups: action.payload };
    default:
      return state;
  }
}

// Provider component
export const PricingSettingsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  
  const formMethods = useForm({
    defaultValues: {
      registrationPricing: [],
      competitionPricing: [],
      registrationLateFeeStartDays: 30,
      competitionLateFeeStartDays: 30,
      includeRegistrationLateFees: false,
      includeLateFees: false,
      includeTimeLimitFees: false
    }
  });

  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    reset, 
    watch, 
    setValue, 
    control,
    getValues 
  } = formMethods;

  const registrationFieldArray = useFieldArray({
    control,
    name: "registrationPricing"
  });

  const competitionFieldArray = useFieldArray({
    control,
    name: "competitionPricing"
  });

  // Watch for form value changes
  const formValues = watch();
  
  useEffect(() => {
    // Only set unsaved changes when we have existing settings AND values are different
    if (state.existingSettings && !state.isLoading) {
      // Do a shallow comparison of the form values with existing settings
      const hasChanges = JSON.stringify(formValues) !== JSON.stringify(state.existingSettings);
      
      if (hasChanges !== state.hasUnsavedChanges) {
        dispatch({ type: ACTIONS.SET_UNSAVED_CHANGES, payload: hasChanges });
      }
    }
  }, [formValues, state.existingSettings, state.isLoading, state.hasUnsavedChanges]);

  // Load pricing settings
  const loadSettings = async () => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const data = await fetchWithAuth(PRICE_SETTINGS_API);
      
      if (data) {
        dispatch({ type: ACTIONS.SET_EXISTING_SETTINGS, payload: data });
        
        // Set form values
        setValue('registrationPricing', data.registrationPricing || []);
        setValue('competitionPricing', data.competitionPricing || []);
        setValue('registrationLateFeeStartDays', data.registrationLateFeeStartDays || 30);
        setValue('competitionLateFeeStartDays', data.competitionLateFeeStartDays || 30);
        setValue('includeRegistrationLateFees', data.includeRegistrationLateFees || false);
        setValue('includeLateFees', data.includeLateFees || false);
        setValue('includeTimeLimitFees', data.includeTimeLimitFees || false);
      }
    } catch (error) {
      console.error('Error fetching pricing settings:', error);
      dispatch({ 
        type: ACTIONS.SET_NOTIFICATION, 
        payload: { type: 'error', message: 'Failed to load pricing settings.' }
      });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      dispatch({ type: ACTIONS.SET_UNSAVED_CHANGES, payload: false });
    }
  };

  // Load age groups from registration rules
  const loadAgeGroups = async () => {
    try {
      dispatch({ type: ACTIONS.SET_LOADING_AGE_GROUPS, payload: true });
      const data = await fetchWithAuth(REGISTRATION_RULES_API);
      if (data && data.ageGroups) {
        dispatch({ type: ACTIONS.SET_AGE_GROUPS, payload: data.ageGroups });
      }
    } catch (error) {
      console.error('Error fetching age groups:', error);
      dispatch({ 
        type: ACTIONS.SET_NOTIFICATION, 
        payload: { type: 'error', message: 'Failed to load age groups from registration rules.' }
      });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING_AGE_GROUPS, payload: false });
    }
  };

  // Initialize both settings and age groups
  const initialize = async () => {
    await loadSettings();
    await loadAgeGroups();
  };

  // Save settings
  const saveSettings = async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    dispatch({ type: ACTIONS.RESET_NOTIFICATION });
    
    try {
      const formData = getValues();
      
      // Prepare data
      const settingsData = {
        ...formData,
        organizerId: state.existingSettings?.organizerId
      };
      
      // Send data to API
      const response = await fetchWithAuth(PRICE_SETTINGS_API, {
        method: 'PUT',
        body: JSON.stringify(settingsData)
      });
      
      dispatch({ 
        type: ACTIONS.SET_NOTIFICATION, 
        payload: { type: 'success', message: 'Pricing settings saved successfully!' }
      });
      
      dispatch({ type: ACTIONS.SET_EXISTING_SETTINGS, payload: {...settingsData} });
      dispatch({ type: ACTIONS.SET_UNSAVED_CHANGES, payload: false });
    } catch (error) {
      console.error('Error saving pricing settings:', error);
      dispatch({ 
        type: ACTIONS.SET_NOTIFICATION, 
        payload: { 
          type: 'error', 
          message: `Failed to save pricing settings: ${error.message}` 
        }
      });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Reset to last saved state
  const resetSettings = () => {
    reset();
    dispatch({ type: ACTIONS.SET_UNSAVED_CHANGES, payload: false });
    
    // Reset to existing settings if available
    if (state.existingSettings) {
      setValue('registrationPricing', state.existingSettings.registrationPricing || []);
      setValue('competitionPricing', state.existingSettings.competitionPricing || []);
      setValue('registrationLateFeeStartDays', state.existingSettings.registrationLateFeeStartDays || 30);
      setValue('competitionLateFeeStartDays', state.existingSettings.competitionLateFeeStartDays || 30);
      setValue('includeRegistrationLateFees', state.existingSettings.includeRegistrationLateFees || false);
      setValue('includeLateFees', state.existingSettings.includeLateFees || false);
      setValue('includeTimeLimitFees', state.existingSettings.includeTimeLimitFees || false);
    }
  };

  // Helper functions for registration pricing
  const addRegistrationPricing = () => {
    if (state.ageGroups.length > 0) {
      // Find age groups not already used
      const availableGroups = state.ageGroups.filter(group => 
        !formValues.registrationPricing.some(field => field.ageGroup === group.label)
      );
      
      if (availableGroups.length > 0) {
        // Add the first available age group
        registrationFieldArray.append({ 
          ageGroup: availableGroups[0].label,
          discountRate: 0,
          lateFee: 0
        });
        return { success: true };
      } else {
        dispatch({ 
          type: ACTIONS.SET_NOTIFICATION, 
          payload: { type: 'info', message: 'All age groups are already added to pricing.' }
        });
        return { success: false };
      }
    } else {
      dispatch({ 
        type: ACTIONS.SET_NOTIFICATION, 
        payload: { type: 'error', message: 'No age groups defined yet. Please add age groups first.' }
      });
      return { success: false };
    }
  };

  // Function to populate all age groups at once
  const populateAllAgeGroups = () => {
    if (state.ageGroups.length === 0) {
      dispatch({ 
        type: ACTIONS.SET_NOTIFICATION, 
        payload: { type: 'error', message: 'No age groups defined in registration rules yet.' }
      });
      return { success: false };
    }
    
    // Only add age groups that aren't already in the pricing table
    const availableGroups = state.ageGroups.filter(group => 
      !formValues.registrationPricing.some(field => field.ageGroup === group.label)
    );
    
    if (availableGroups.length === 0) {
      dispatch({ 
        type: ACTIONS.SET_NOTIFICATION, 
        payload: { type: 'info', message: 'All age groups are already added to pricing.' }
      });
      return { success: false };
    }
    
    // Add all available age groups
    availableGroups.forEach(group => {
      registrationFieldArray.append({ 
        ageGroup: group.label, 
        discountRate: 0,
        lateFee: 0
      });
    });
    
    dispatch({ 
      type: ACTIONS.SET_NOTIFICATION, 
      payload: { type: 'success', message: `Added ${availableGroups.length} age groups to pricing.` }
    });
    
    return { success: true, count: availableGroups.length };
  };

  // Add competition pricing
  const addCompetitionPricing = () => {
    competitionFieldArray.append({ 
      category: '', 
      minDancers: 1,
      maxDancers: 1,
      pricingType: 'perDancer', // default to per dancer pricing
      discountRate: 0,
      lateFee: 0,
      timeLimit: '0:00',
      timeLimitFee: 0
    });
    return { success: true };
  };

  // Duplicate registration pricing
  const duplicateRegistrationPricing = (index) => {
    const item = formValues.registrationPricing[index];
    registrationFieldArray.append({
      ...item,
      ageGroup: `${item.ageGroup} (Copy)`
    });
    return { success: true };
  };

  // Duplicate competition pricing
  const duplicateCompetitionPricing = (index) => {
    const item = formValues.competitionPricing[index];
    competitionFieldArray.append({
      ...item,
      category: `${item.category} (Copy)`
    });
    return { success: true };
  };

  // Provide the context value
  const contextValue = {
    state,
    formMethods,
    formValues,
    errors,
    register,
    control,
    setValue,
    registrationFieldArray,
    competitionFieldArray,
    loadSettings,
    loadAgeGroups,
    initialize,
    saveSettings,
    resetSettings,
    addRegistrationPricing,
    populateAllAgeGroups,
    addCompetitionPricing,
    duplicateRegistrationPricing,
    duplicateCompetitionPricing,
    calculateTotalPrice,
    handleSubmit
  };

  return (
    <PricingSettingsContext.Provider value={contextValue}>
      {children}
    </PricingSettingsContext.Provider>
  );
};

// Custom hook to use the context
export const usePricingSettings = () => {
  const context = useContext(PricingSettingsContext);
  if (!context) {
    throw new Error('usePricingSettings must be used within a PricingSettingsProvider');
  }
  return context;
}; 